import { format, FormatOptions, isSameYear, isValid } from 'date-fns';
import { getLocale } from './datefns-locales';
import { getAgencyLanguageId } from './languages';

type FormatDateOptions = FormatOptions & { fallBackStr?: string };

/**
 * A wrapper for the date-fns `format` function.
 *
 * - injects as `locale` the current agency language;
 * - has a try/catch mechanism to never throw an error if input date is not defined, null, or an invalid date; in this case, `options.fallBackStr` will be returned
 * @param date
 * @param formatStr
 * @param options
 * @returns {string} the formatted date
 */
export function formatDate(
    date: string | number | Date | null | undefined,
    formatStr: string,
    options?: FormatDateOptions
): string {
    const fallBackStr = options?.fallBackStr ?? '';
    try {
        if (!date) {
            throw new Error('formatDate: null/undefined date.');
        }
        const locale = options?.locale ?? getLocale(getAgencyLanguageId());
        const parsedDate = date instanceof Date ? date : new Date(date);

        if (isValid(parsedDate)) {
            return format(parsedDate, formatStr, { locale, ...options });
        } else {
            throw new Error('formatDate: invalid date.');
        }
    } catch (error) {
        return fallBackStr;
    }
}

/** Simple util returning `true` if the input date is in the current year, `false` otherwise (or if the input date is invalid) */
export function isCurrentYear(date: string | number | Date): boolean {
    const parsedDate = date instanceof Date ? date : new Date(date);

    if (!isValid(parsedDate)) {
        return false;
    }

    return isSameYear(new Date(), parsedDate);
}
